import React from "react";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { Box, LinearProgress, Typography } from "@mui/material";
import { Fleet } from "../../../../types/fleets.types";
import EditFleet from "../EditFleet/EditFleet";
import DeleteFleet from "../DeleteFleet/DeleteFleet";
import ViewFleetSettings from "../ViewFleetSettings/ViewFleetSettings";
import ViewInvoices from "../ViewInvoices/ViewInvoices";
import { ActionsCell } from "../../../../components/Table";

const UsageBar: React.FC<{ label: string; usage: number; limit: number }> = ({ label, usage, limit }) => {
  const isUnlimited = limit >= 99999;
  const percent = isUnlimited ? 0 : Math.min((usage / limit) * 100, 100);
  const barColor = percent >= 80 ? "error" : percent >= 50 ? "warning" : "success";

  return (
    <Box sx={{ minWidth: 140 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {usage} / {isUnlimited ? "∞" : limit}
        </Typography>
      </Box>
      {!isUnlimited && (
        <LinearProgress variant="determinate" value={percent} color={barColor} sx={{ borderRadius: 4, height: 6 }} />
      )}
    </Box>
  );
};

const formatBRL = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const CreditBar: React.FC<{ credit: number; balance: number }> = ({ credit, balance }) => {
  const percent = balance > 0 ? Math.min((credit / balance) * 100, 100) : 0;
  const barColor = percent >= 80 ? "error" : percent >= 50 ? "warning" : "success";

  return (
    <Box sx={{ minWidth: 200 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
        <Typography variant="caption" color="text.secondary">Pré pago</Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {formatBRL(credit)} / {formatBRL(balance)}
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={percent} color={barColor} sx={{ borderRadius: 4, height: 6 }} />
    </Box>
  );
};

interface Props {
  fleet: Fleet;
}

const VehicleRow: React.FC<Props> = ({ fleet }: Props) => {
  return (
    <TableRow
      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      hover
    >
      <TableCell component="th" scope="row" sx={{ whiteSpace: "nowrap" }}>
        {fleet.client?.name ?? "—"}
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{fleet.description}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{fleet.washingType?.name ?? "—"}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <UsageBar label="Semanal" usage={fleet.weeklyUsage ?? 0} limit={fleet.weeklyLimit} />
          <UsageBar label="Mensal" usage={fleet.monthlyUsage ?? 0} limit={fleet.monthlyLimit} />
        </Box>
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {fleet.isCreditAllowed && (
            <CreditBar credit={fleet.credit ?? 0} balance={fleet.balance ?? 0} />
          )}
          <Box sx={{ minWidth: 200, display: "flex", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.secondary">Pós pago</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {formatBRL(fleet.debit ?? 0)}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
        <ActionsCell style={{ justifyContent: "flex-end" }}>
          <ViewFleetSettings fleet={fleet} />
          <ViewInvoices fleet={fleet} />
          <DeleteFleet fleet={fleet} />
          <EditFleet fleet={fleet} />
        </ActionsCell>
      </TableCell>
    </TableRow>
  );
};

export default VehicleRow;
